import { useEffect, useRef, useState } from 'react'
import type { Participant } from 'livekit-client'

import { CheckIcon, CloseIcon, SpinnerIcon } from '@/src/assets/icons'

import { useMaybeRoomContext } from '@/src/context'
import { clientLogError, clientLogInfo, clientLogWarn } from '@/src/lib/clientLog'

import { useAppSelector } from '@/src/store/hooks'
import { selectRoomState, selectV } from '@/src/store/slices/room'
import { selectBranding, selectIsAdmin } from '@/src/store/slices/meetData'
import { selectAccessToken } from '@/src/store/slices/token'
import { buildQuestionSessionId } from '@/src/prefabs/textAgentQuestionCatalog'
import {isCandidateForQuestionSession} from '@/src/lib/companyParticipant'

/** Сколько ждать пропажи строки из списка, прежде чем снова разрешить клик. */
const ADMIT_WAIT_MS = 30_000

/** @public */
export interface WaitListItemProps {
  participant: Participant
}

/**
 * Строка человека в зале ожидания: впустить или отклонить.
 *
 * Впуск идёт через `/api/admit_participant`, а не через DataChannel.
 * Кнопка крутит спиннер, пока LiveKit не снимет waiting-права или не
 * пройдёт полминуты: иначе организатор снова жмёт вхолостую и не видит,
 * дошёл ли запрос.
 */
export function WaitListItem({ participant }: WaitListItemProps) {
  const roomContext = useMaybeRoomContext()
  const { livekit_url } = useAppSelector(selectRoomState)
  const { text_primary_color } = useAppSelector(selectBranding)
  const isAdmin = useAppSelector(selectIsAdmin)
  const accessToken = useAppSelector(selectAccessToken)
  const v = useAppSelector(selectV)
  const [isAdmitting, setIsAdmitting] = useState(false)
  const isAdmittingRef = useRef(false)
  const admitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (admitTimeoutRef.current) {
        clearTimeout(admitTimeoutRef.current)
      }
    }
  }, [])

  /**
   * Снимает спиннер и таймер ожидания, чтобы кнопку снова можно было нажать.
   */
  const stopAdmitting = () => {
    if (admitTimeoutRef.current) {
      clearTimeout(admitTimeoutRef.current)
      admitTimeoutRef.current = null
    }

    isAdmittingRef.current = false
    setIsAdmitting(false)
  }

  /**
   * Просит сервер выдать гостю publish/subscribe и ждёт, пока строка исчезнет.
   *
   * Если LiveKit обновит права раньше таймаута, компонент размонтируется сам:
   * список ожидания строится из `canPublish` и `canSubscribe`.
   */
  const addToRoom = async () => {
    const roomName = roomContext?.name
    const targetIdentity = participant.identity
    const localIdentity = roomContext?.localParticipant?.identity

    if (isAdmittingRef.current) {
      return
    }

    if (!livekit_url) {
      clientLogWarn('waiting-room-admit click skipped: no livekit_url', {
        room: roomName,
        targetIdentity,
        localIdentity,
        hasLivekitUrl: false,
      })

      return
    }

    isAdmittingRef.current = true
    setIsAdmitting(true)

    clientLogInfo('waiting-room-admit click', {
      room: roomName,
      targetIdentity,
      localIdentity,
      hasLivekitUrl: true,
    })

    admitTimeoutRef.current = setTimeout(() => {
      isAdmittingRef.current = false
      setIsAdmitting(false)
      admitTimeoutRef.current = null

      clientLogError('waiting-room-admit timeout: participant still in waiting list', {
        room: roomName,
        targetIdentity,
        localIdentity,
      })
    }, ADMIT_WAIT_MS)

    try {
      const response = await fetch(
        `${window.origin}/api/admit_participant?livekit_url=${livekit_url}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            room: roomName,
            identity: targetIdentity,
            token: accessToken,
          }),
        },
      )

      if (!response.ok) {
        clientLogError('waiting-room-admit api error', {
          room: roomName,
          targetIdentity,
          localIdentity,
          status: response.status,
        })
        stopAdmitting()

        return
      }

      clientLogInfo('waiting-room-admit api success', {
        room: roomName,
        targetIdentity,
        localIdentity,
        status: response.status,
      })

      // В HR-комнате каталог вопросов завязан на сессию кандидата.
      // Её id по-прежнему уходит по DataChannel: впуск уже сделал сервер,
      // этот пакет нужен панели, а не для перезахода гостя.
      if (v === 'hr' && roomContext && isCandidateForQuestionSession(participant.attributes)) {
        try {
          const timestamp = Date.now()
          const questionSessionId = buildQuestionSessionId(
            participant.identity,
            participant.sid,
            timestamp,
          )

          await roomContext.localParticipant.publishData(
            new TextEncoder().encode(
              JSON.stringify({
                action: 'reconnect_with_elevated_permissions',
                targetParticipant: participant.identity,
                roomName: roomContext.name,
                participantName: participant.name,
                timestamp,
                questionSessionId,
              }),
            ),
            { topic: 'admin_commands' },
          )
        } catch (catalogError) {
          clientLogWarn('waiting-room-admit hr catalog session publish failed', {
            room: roomName,
            targetIdentity,
            localIdentity,
            error: catalogError instanceof Error ? catalogError.message : String(catalogError),
          })
        }
      }
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e))

      clientLogError('waiting-room-admit api throw', {
        room: roomName,
        targetIdentity,
        localIdentity,
        error: error.message,
      })
      stopAdmitting()
    }
  }

  /**
   * Удаляет человека из комнаты через `/api/remove_participant`.
   */
  const removeFromRoom = async () => {
    const payload = {
      room: roomContext.name,
      identity: participant.identity,
      token: accessToken,
      localParticipant: roomContext.localParticipant,
    }

    if (!livekit_url) return

    try {
      await fetch(`${window.origin}/api/remove_participant?livekit_url=${livekit_url}`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }).then((res) => {
        console.log('Log during remove participant: \n', res)
      })
    } catch (e) {
      const error: Error = e
      console.error(`Something went wrong with remove participant: \n${error}`)
    }
  }

  return (
    <div className="lk-participant-item lk-waiting-item">
      <div
        className="lk-participant-item-avatar"
        style={{ background: 'rgba(255, 255, 255, 0.25)' }}
      >
        {participant.name
          ?.split(' ')
          .map((item) => item.slice(0, 1).toUpperCase())
          .join('')
          .slice(0, 2)}
      </div>

      <div className="lk-participant-item-name lk-waiting-name" style={{ color: `${text_primary_color}` }}>
        {participant.name}
      </div>

      {isAdmin && (
        <div className="lk-waiting-actions">
          <button
            className="button-sm"
            onClick={addToRoom}
            disabled={isAdmitting}
            aria-label="admit participant"
          >
            {isAdmitting ? (
              <SpinnerIcon className="lk-spinner" style={{ color: text_primary_color }} />
            ) : (
              <CheckIcon fill={text_primary_color} />
            )}
          </button>

          <button className="button-sm" onClick={removeFromRoom}>
            <CloseIcon />
          </button>
        </div>
      )}
    </div>
  )
}
